import { PreviewOption } from '../../services/videoPreviewService';
import { MediaItem, SpecialEffect } from '../../types';

/**
 * Handle preview option selection
 * 
 * Process:
 * 1. Get preview segments for selected option from allMediaItemsFromDB
 * 2. Copy them and remove preview-specific fields (clean them)
 * 3. Position them at the start of timeline (0, sequential)
 * 4. Shift all other media items to the right by total preview duration
 * 5. Return combined timeline: [previews] + [shifted originals]
 */
export const handlePreviewSelection = (
  option: PreviewOption,
  allMediaItemsFromDB: MediaItem[], // Full DB data including preview segments
  currentMediaItems: MediaItem[], // Current timeline (originals only)
  onMediaItemsUpdate: (items: MediaItem[]) => void
) => {
  console.log('🎬 Applying preview:', option.name);
  
  // Get preview segments for this option
  const previewSegments = allMediaItemsFromDB.filter(item => 
    item.effect?.includes(`preview-segment|${option.id}`)
  );

  if (previewSegments.length === 0) {
    console.warn('No preview segments found');
    return;
  }

  console.log(`Found ${previewSegments.length} preview segments`);
  
  // Sort by videoOffset to maintain order
  const sortedPreviewSegments = [...previewSegments].sort((a, b) => {
    const aStart = a.videoOffset !== undefined ? a.videoOffset : a.start;
    const bStart = b.videoOffset !== undefined ? b.videoOffset : b.start;
    return aStart - bStart;
  });

  // Copy and clean preview segments
  let timelinePosition = 0;
  const cleanedPreviewItems: MediaItem[] = sortedPreviewSegments.map((segment, index) => {
    const duration = segment.end - segment.start;
    const isLastSegment = index === sortedPreviewSegments.length - 1;
    
    const cleanedItem: MediaItem = {
      ...segment,
      id: `preview-applied-${option.id}-${index}-${Date.now()}`,
      effect: SpecialEffect.NONE,
      start: timelinePosition,
      end: timelinePosition + duration,
      length: duration,
      hidden: false,
      videoOffset: segment.videoOffset !== undefined ? segment.videoOffset : timelinePosition,
      videoDuration: segment.videoDuration || duration,
      
      // NEW: Apply black & white effect to ALL preview items
      saturation: 0, // 0 = black & white
      
      // NEW: Apply fade transition to LAST preview item
      ...(isLastSegment && {
        transitionType: 'fade',
        transitionDuration: 1.0 // 1 second fade
      })
    };
    
    // Advance timeline position, accounting for transition overlap
    timelinePosition += duration - (segment.transitionDuration || 0);
    return cleanedItem;
  });

  console.log(`✅ Applied effects: B&W to all ${cleanedPreviewItems.length} segments, fade to last segment`);

  const totalPreviewDuration = timelinePosition;

  // Shift all current (original) items to the right
  const shiftedOriginals = currentMediaItems.map(item => ({
    ...item,
    start: item.start + totalPreviewDuration,
    end: item.end + totalPreviewDuration,
  }));

  // Combine: previews first, then shifted originals
  const newTimeline = [...cleanedPreviewItems, ...shiftedOriginals];
  
  console.log(`✅ Applied: ${cleanedPreviewItems.length} preview + ${shiftedOriginals.length} original items`);
  
  onMediaItemsUpdate(newTimeline);
};

/**
 * Clear preview - restore to initial state
 * 
 * Simply restore the saved initial media items (originals without previews)
 */
export const handleClearPreview = (
  initialMediaItems: MediaItem[],
  onMediaItemsUpdate: (items: MediaItem[]) => void
) => {
  console.log(`🧹 Clearing preview - restoring ${initialMediaItems.length} items`);
  onMediaItemsUpdate([...initialMediaItems]);
};

/**
 * Synchronous version that returns the new timeline directly
 * Use this with functional setState: setMediaItems(prev => handlePreviewSelectionSync(...))
 */
export const handlePreviewSelectionSync = (
  option: PreviewOption,
  allMediaItemsFromDB: MediaItem[], // Full DB data including preview segments
  currentMediaItems: MediaItem[] // Current timeline (originals only)
): MediaItem[] => {
  console.log('🎬 Applying preview (sync):', option.name);
  console.log('🔍 Looking for option ID:', option.id);
  console.log('🔍 Total items in DB:', allMediaItemsFromDB.length);
  console.log('🔍 Items with preview-segment effect:', 
    allMediaItemsFromDB.filter(item => item.effect?.includes('preview-segment')).length
  );
  
  // Debug: Show all effect values
  const effectsPreview = allMediaItemsFromDB
    .filter(item => item.effect?.includes('preview'))
    .map(item => ({ name: item.name, effect: item.effect }));
  console.log('🔍 All preview effects:', effectsPreview);
  
  // Get preview segments for this option
  const previewSegments = allMediaItemsFromDB.filter(item => 
    item.effect?.includes(`preview-segment|${option.id}`)
  );

  if (previewSegments.length === 0) {
    console.warn('⚠️ No preview segments found for option:', option.id);
    console.warn('⚠️ Available preview segment IDs:', 
      allMediaItemsFromDB
        .filter(item => item.effect?.includes('preview-segment'))
        .map(item => {
          const match = item.effect?.match(/preview-segment\|([^|]+)/);
          return match ? match[1] : 'unknown';
        })
    );
    return currentMediaItems; // Return unchanged if no segments
  }

  console.log(`Found ${previewSegments.length} preview segments`);
  
  // Sort by videoOffset to maintain order
  const sortedPreviewSegments = [...previewSegments].sort((a, b) => {
    const aStart = a.videoOffset !== undefined ? a.videoOffset : a.start;
    const bStart = b.videoOffset !== undefined ? b.videoOffset : b.start;
    return aStart - bStart;
  });

  // Copy and clean preview segments
  let timelinePosition = 0;
  const cleanedPreviewItems: MediaItem[] = sortedPreviewSegments.map((segment, index) => {
    const duration = segment.end - segment.start;
    const isLastSegment = index === sortedPreviewSegments.length - 1;
    
    const cleanedItem: MediaItem = {
      ...segment,
      id: `preview-applied-${option.id}-${index}-${Date.now()}`,
      effect: SpecialEffect.NONE,
      start: timelinePosition,
      end: timelinePosition + duration,
      length: duration,
      hidden: false,
      videoOffset: segment.videoOffset !== undefined ? segment.videoOffset : timelinePosition,
      videoDuration: segment.videoDuration || duration,
      
      // NEW: Apply black & white effect to ALL preview items
      saturation: 0, // 0 = black & white
      
      // NEW: Apply fade transition to LAST preview item
      ...(isLastSegment && {
        transitionType: 'fade',
        transitionDuration: 1.0 // 1 second fade
      })
    };
    
    // Advance timeline position, accounting for transition overlap
    timelinePosition += duration - (segment.transitionDuration || 0);
    return cleanedItem;
  });

  console.log(`✅ Applied effects: B&W to all ${cleanedPreviewItems.length} segments, fade to last segment`);

  const totalPreviewDuration = timelinePosition;

  // Shift all current (original) items to the right
  const shiftedOriginals = currentMediaItems.map(item => ({
    ...item,
    start: item.start + totalPreviewDuration,
    end: item.end + totalPreviewDuration,
  }));

  // Combine: previews first, then shifted originals
  const newTimeline = [...cleanedPreviewItems, ...shiftedOriginals];
  
  console.log(`✅ Applied (sync): ${cleanedPreviewItems.length} preview + ${shiftedOriginals.length} original items`);
  
  return newTimeline;
};
